<template>
  <div class="moneda-configuracion card p-4">

    <h3 class="mb-4 text-primary">
      <font-awesome-icon :icon="['fas', 'money-bill-wave']" class="me-2" />
      Configuración de Moneda del Sistema
    </h3>

    <!-- Moneda actual -->
    <div v-if="monedaActual" class="mb-4 alert alert-info">
      <strong>Moneda actual:</strong>
      {{ monedaActual.nombre }} ({{ monedaActual.codigo }})<br />
      <small>Desde: {{ formatoFecha(monedaActual.fecha_inicio) }}</small>
    </div>
    <div v-else class="mb-4 alert alert-warning">
      ⚠️ Aún no se ha configurado una moneda actual para este club.
    </div>



    <!-- Selección nueva moneda -->
    <div class="mb-4">
      <label class="form-label fw-bold">
        Seleccionar nueva moneda del sistema
      </label>
      <div class="input-group">
        <select class="form-select" v-model="nuevaMonedaSeleccionada">
          <option disabled value="">Selecciona una moneda...</option>
          <option v-for="moneda in monedasOficiales" :key="moneda.id" :value="moneda.id">
            {{ moneda.descripcion }} ({{ moneda.id }})
          </option>
        </select>
        <button class="btn btn-outline-primary" :disabled="!nuevaMonedaSeleccionada" @click="cambiarMoneda">
          Establecer como actual
        </button>
      </div>
    </div>

    <!-- Buscador -->
    <div class="mb-3">
      <input type="text" v-model="busqueda" class="form-control"
        placeholder="Buscar historial por nombre o código..." />
    </div>

    <h5 class="mb-3">Historial de monedas usadas</h5>

    <!-- Lista historial -->
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border text-primary" role="status"></div>
    </div>

    <div v-else>
      <ul class="list-group mb-3">
        <li v-for="moneda in paginatedHistorial" :key="moneda.id"
          class="list-group-item d-flex justify-content-between align-items-center"
          :class="{ 'list-group-item-success': moneda.es_actual }">
          <div>
            <strong>{{ moneda.nombre }}</strong> ({{ moneda.codigo }})
            <div class="small text-muted">
              Desde {{ formatoFecha(moneda.fecha_inicio) }}
              <span v-if="moneda.fecha_fin"> hasta {{ formatoFecha(moneda.fecha_fin) }}</span>
            </div>
            <span v-if="moneda.es_actual" class="badge bg-success ms-2">Actual</span>
          </div>
        </li>
      </ul>

      <!-- Paginación -->
      <nav v-if="totalPages > 1" class="mt-3">
        <ul class="pagination pagination-sm justify-content-center">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <button class="page-link" @click="currentPage--">Anterior</button>
          </li>
          <li v-for="n in totalPages" :key="n" class="page-item" :class="{ active: currentPage === n }">
            <button class="page-link" @click="currentPage = n">{{ n }}</button>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <button class="page-link" @click="currentPage++">Siguiente</button>
          </li>
        </ul>
      </nav>
    </div>

    <div v-if="mensaje" class="alert alert-success mt-3">
      {{ mensaje }}
    </div>

  </div>
</template>

<script>
import axios from '@/axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'MonedaConfiguracion',
  components: { FontAwesomeIcon },

  data() {
    return {
      monedaActual: null,
      historialMonedas: [],
      monedasOficiales: [],
      nuevaMonedaSeleccionada: '',
      busqueda: '',
      loading: false,
      mensaje: '',
      currentPage: 1,
      pageSize: 8,
      club_id: this.$store.getters.getClubActual.id
    };
  },
  watch: {
    busqueda() {
      this.currentPage = 1;
    }
  },
  computed: {
    historialFiltrado() {
      const term = this.busqueda.toLowerCase();
      return this.historialMonedas.filter(m =>
        m.nombre.toLowerCase().includes(term) ||
        m.codigo.toLowerCase().includes(term)
      );
    },
    paginatedHistorial() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.historialFiltrado.slice(start, start + this.pageSize);
    },
    totalPages() {
      return Math.ceil(this.historialFiltrado.length / this.pageSize);
    }
  },
  methods: {
    formatoFecha(fecha) {
      if (!fecha) return '—';
      return new Date(fecha).toLocaleDateString();
    },
    async fetchDatos() {
      this.loading = true;
      this.monedaActual = null;
      try {
        const [historial, constantes] = await Promise.all([
          axios.get('/api/moneda', { params: { club_id: this.club_id } }),
          axios.get('/api/constantes/tipos_de_moneda')
        ]);

        this.historialMonedas = historial.data;
        this.monedasOficiales = constantes.data;

        try {
          const monedaActual = await axios.get('/api/moneda_actual', {
            params: { club_id: this.club_id }
          });
          this.monedaActual = monedaActual.data;
        } catch (err) {
          if (err.response && err.response.status === 404) {
            this.monedaActual = null;
            this.mensaje = '⚠️ Aún no hay una moneda configurada como actual.';
          } else {
            throw err;
          }
        }

      } catch (err) {
        console.error('Error al cargar datos:', err);
        alert('Error al cargar los datos del sistema de monedas.');
      } finally {
        this.loading = false;
      }
    },

    async cambiarMoneda() {
      if (!this.nuevaMonedaSeleccionada) return;
      if (!confirm('¿Estás seguro que deseas cambiar la moneda actual?')) return;

      const moneda = this.monedasOficiales.find(m => m.id === this.nuevaMonedaSeleccionada);
      if (!moneda) {
        alert('Moneda no válida.');
        return;
      }

      try {
        await axios.post('/api/moneda_actual', {
          club_id: this.club_id,
          nombre: moneda.descripcion,
          codigo: moneda.id,
          simbolo: moneda.simbolo || '', // por si no viene
          observacion: ''
        });

        this.mensaje = 'Moneda actualizada correctamente.';
        this.nuevaMonedaSeleccionada = '';
        await this.fetchDatos();

        setTimeout(() => {
          this.mensaje = '';
        }, 3000);
      } catch (err) {
        console.error('Error al cambiar moneda:', err);
        alert('No se pudo cambiar la moneda.');
      }
    }

  },
  mounted() {
    this.fetchDatos();
  }
};
</script>

<style scoped>
.moneda-configuracion {
  max-width: 700px;
  margin: auto;
}

.list-group-item-success {
  background-color: #d1e7dd;
}

.alert-info {
  background-color: #e8f4fc;
  color: #0c5460;
}

.pagination .page-item.active .page-link {
  background-color: #0d6efd;
  color: white;
}

.pagination .page-link {
  color: #0d6efd;
}
</style>
